import type { RewardHubStatus, RewardClaimResult, RewardAdAction, RewardHubConfig, RewardAdTicket } from "@evolution/shared";
import { reconcileEarnedTestRewards } from "../ads/pending-test-rewards";
import { notifyQuestRewardsChanged } from "../game/quest-reward-notifications";
import { Capacitor } from "@capacitor/core";
import { clearGoogleSignInState } from "../auth/google-identity";
import type { AndroidUpdateNotice, FeatureFlags, FriendConversation, FriendMessage, FriendReadReceipt, GameMode, PrivateLobbySettings, PrivateLobbySnapshot, PrivateLobbyTeam, PublicProfile } from "@evolution/shared";
import type { PublicEconomyConfig } from "@evolution/shared";
import type { SocialFriendRequest, SocialPlayerProfile } from "@evolution/shared";
import { createSecureUuid } from "../game/secure-random";
import { selectArenaEntryCapabilities } from "../game/arena-entry-capabilities";
import { normalizeAuthEmail, type AuthValidationIssue } from "../auth/registration-feedback";
import { parseWarriorJourneyStatus } from "../game/warrior-journey-status";

export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly issues: readonly AuthValidationIssue[] = []) {
    super(message);
  }
}

export interface NativeSessionTokens {
  accessToken: string;
  refreshToken: string;
  accessExpiresInMs: number;
  refreshExpiresInMs: number;
}

const NATIVE_SESSION_KEY = "ea-native-session-v1";
const NATIVE_ORIGIN = "http://localhost";
const configuredApiOrigin = (import.meta.env.VITE_API_URL as string | undefined)?.trim() ?? "";
const configuredArenaUrl = (import.meta.env.VITE_WS_URL as string | undefined)?.trim() ?? "";

export const resolveApiEndpoint = (path: string, apiOrigin = configuredApiOrigin): string => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const normalizedOrigin = apiOrigin.replace(/\/+$/, "");
  return normalizedOrigin ? `${normalizedOrigin}${normalizedPath}` : normalizedPath;
};

export const resolveArenaEndpoint = (
  arenaUrl = configuredArenaUrl,
  location: Pick<Location, "protocol" | "host"> = window.location,
): string => {
  const configured = arenaUrl.replace(/\/+$/, "");
  if (configured) return configured.replace(/^http:/i, "ws:").replace(/^https:/i, "wss:");
  const protocol = location.protocol === "https:" ? "wss" : "ws";
  return `${protocol}://${location.host}/ws`;
};

export const isNativeAndroidClient = (): boolean => {
  try {
    return Capacitor.isNativePlatform()
      && Capacitor.getPlatform() === "android"
      && window.location.origin === NATIVE_ORIGIN;
  } catch {
    return false;
  }
};

const isNativeSessionTokens = (value: unknown): value is NativeSessionTokens => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<NativeSessionTokens>;
  return typeof candidate.accessToken === "string" && candidate.accessToken.length > 0
    && typeof candidate.refreshToken === "string" && candidate.refreshToken.length >= 32
    && typeof candidate.accessExpiresInMs === "number" && candidate.accessExpiresInMs > 0
    && typeof candidate.refreshExpiresInMs === "number" && candidate.refreshExpiresInMs > 0;
};

const readNativeSession = (): NativeSessionTokens | null => {
  if (!isNativeAndroidClient()) return null;
  try {
    const stored = localStorage.getItem(NATIVE_SESSION_KEY);
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    if (isNativeSessionTokens(parsed)) return parsed;
    localStorage.removeItem(NATIVE_SESSION_KEY);
  } catch {
    // Capacitor storage can be unavailable briefly while the WebView restores.
  }
  return null;
};

const storeNativeSession = (tokens: NativeSessionTokens): void => {
  if (!isNativeAndroidClient()) return;
  try {
    // This origin belongs to the sandboxed bundled WebView. Browser sessions
    // keep using HttpOnly cookies and never write bearer tokens here.
    localStorage.setItem(NATIVE_SESSION_KEY, JSON.stringify(tokens));
  } catch {
    // The next protected call will request another sign-in if storage is off.
  }
};

const clearNativeSession = (): void => {
  if (!isNativeAndroidClient()) return;
  try {
    localStorage.removeItem(NATIVE_SESSION_KEY);
  } catch {
    // Nothing else can be cleared while WebView storage is unavailable.
  }
};

const captureNativeSession = (payload: unknown): boolean => {
  if (!payload || typeof payload !== "object") return false;
  const tokens = (payload as { tokens?: unknown }).tokens;
  if (!isNativeSessionTokens(tokens)) return false;
  storeNativeSession(tokens);
  return true;
};

const nativeHeaders = (headers?: HeadersInit, includeAccessToken = true): Headers => {
  const result = new Headers(headers);
  result.set("x-ea-client", "android");
  const accessToken = includeAccessToken ? readNativeSession()?.accessToken : null;
  if (accessToken) result.set("authorization", `Bearer ${accessToken}`);
  return result;
};

let refreshInFlight: Promise<boolean> | null = null;

const performRefresh = async (native: boolean): Promise<boolean> => {
  const tokens = native ? readNativeSession() : null;
  if (native && !tokens) return false;
  const headers = native ? nativeHeaders(undefined, false) : new Headers();
  if (native) headers.set("content-type", "application/json");
  const response = await fetch(resolveApiEndpoint("/api/auth/refresh"), {
    method: "POST",
    credentials: native ? "omit" : "include",
    headers,
    ...(native ? { body: JSON.stringify({ refreshToken: tokens!.refreshToken }) } : {}),
  });
  if (!response.ok) {
    if (native) clearNativeSession();
    return false;
  }
  if (!native) return true;
  const payload: unknown = await response.json().catch(() => null);
  if (captureNativeSession(payload)) return true;
  clearNativeSession();
  return false;
};

const refreshSession = async (native: boolean): Promise<boolean> => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = performRefresh(native);
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
};

export const request = async <T>(path: string, init: RequestInit = {}, expectedStatus?: number): Promise<T> => {
  const native = isNativeAndroidClient();
  const execute = () => {
    const headers = native ? nativeHeaders(init.headers) : new Headers(init.headers);
    if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
    return fetch(resolveApiEndpoint(path), {
      ...init,
      credentials: native ? "omit" : "include",
      headers,
    });
  };
  let response = await execute();
  if (response.status === 401 && path !== "/api/auth/refresh" && path !== "/api/auth/login" && path !== "/api/auth/deletion-login" && path !== "/api/auth/google" && path !== "/api/auth/google/existing" && path !== "/api/auth/google/link") {
    if (await refreshSession(native)) response = await execute();
  }
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    const body = payload && typeof payload === 'object' && !Array.isArray(payload)
      ? payload as { error?: unknown; issues?: unknown } : null;
    const issues: AuthValidationIssue[] = Array.isArray(body?.issues)
      ? body.issues.slice(0, 12).flatMap((issue: unknown) => {
        if (!issue || typeof issue !== 'object') return [];
        const item = issue as Partial<AuthValidationIssue>;
        return typeof item.path === 'string' && item.path.length <= 100
          && typeof item.message === 'string' && item.message.length <= 300
          ? [{ path: item.path, message: item.message }] : [];
      }) : [];
    throw new ApiError(response.status, typeof body?.error === 'string' ? body.error : "Server unavailable", issues);
  }
  if (expectedStatus !== undefined && response.status !== expectedStatus) {
    throw new ApiError(502, "Account deletion was not confirmed by the server.");
  }
  if (response.status === 204) return undefined as T;
  const payload: unknown = await response.json();
  // A dismissed native welcome must not persist a late identity response.
  if (init.signal?.aborted) throw new DOMException('Request cancelled', 'AbortError');
  if (native) captureNativeSession(payload);
  return payload as T;
};

export interface SessionResponse {
  profile: PublicProfile;
  guest: boolean;
  inventory?: string[];
  loadout?: Record<string, string>;
  tokens?: NativeSessionTokens;
}

export interface AccountSecurity {
  profile: PublicProfile;
  guest: boolean;
  hasPassword: boolean;
  providers: Array<"google">;
}

export const ACCOUNT_SERVICE_UNAVAILABLE = "Account service is out of date or unavailable. Please try again later.";

export const isAccountSecurity = (value: unknown): value is AccountSecurity => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const candidate = value as Partial<AccountSecurity>;
  if (typeof candidate.guest !== "boolean" || typeof candidate.hasPassword !== "boolean"
    || !Array.isArray(candidate.providers) || candidate.providers.some((provider) => provider !== "google")) return false;
  const profile = candidate.profile;
  if (!profile || typeof profile !== "object" || Array.isArray(profile)
    || typeof profile.id !== "string" || !profile.id.trim() || profile.id.length > 200
    || typeof profile.username !== "string" || !profile.username.trim() || profile.username.length > 200) return false;
  const numericFields = ["accountLevel", "accountXp", "totalMatches", "totalKills", "bestScore", "highestEvolution", "bestKillStreak", "totalSurvivalSeconds", "winCount", "gold", "crystals"] as const;
  return numericFields.every((field) => typeof profile[field] === "number" && Number.isFinite(profile[field]) && profile[field] >= 0)
    && (profile.seasonRank === null || (typeof profile.seasonRank === "number" && Number.isFinite(profile.seasonRank) && profile.seasonRank >= 0));
};

export interface DeleteAccountRequest {
  confirmation: "DELETE";
  currentPassword?: string;
  credential?: string;
}

export interface FriendSummary {
  id: string;
  publicId?: string;
  online?: boolean;
  username: string;
  accountLevel: number;
  highestEvolution: number;
  friendsSince: string;
  /** Optional while web and API releases roll independently. */
  unreadCount?: number;
}

export interface SocialSnapshot {
  revision: string;
  friends: FriendSummary[];
  requests: SocialFriendRequest[];
  acceptedInvites: Array<{ friendId: string; message: FriendMessage }>;
}
export type FriendArenaEntry = Pick<import('@evolution/shared').FriendGameInvitation, 'mode' | 'mapChoice'>;

export interface FriendInviteCreated {
  inviteCode: string;
  partyCode: string;
  expiresAt: string;
  inviter: { id: string; username: string };
}

export interface FriendInviteAccepted {
  friend: FriendSummary;
  partyCode: string;
  alreadyFriends: boolean;
  acceptedAt: string;
}

export interface MatchTicket {
  mapProtocol?: 1 | 2;
  mapId?: import('@evolution/shared').ArenaMapId;
  arena?: { width: number; height: number };
  seatReservation?: { name: string; roomId: string; processId: string; sessionId: string; publicAddress?: string };
  ticket: string;
  roomName: string;
  userId: string;
  username: string;
  expiresInMs: number;
  partyCode: string;
  arenaRevision?: 1 | 2 | 3;
  mode?: GameMode;
  quickPlaylist?: QuickPlaylistSnapshot;
}

export interface QuickPlaylistSnapshot {
  id: string;
  revision: number;
  mode: "classic" | "blitz";
  startsAt: number;
  endsAt: number;
}

export interface PublicArenaStatus {
  playersOnline: number;
  activeArenas: number;
  region: string;
  runtime: "demo" | "development" | "production" | "durable";
  generatedAt: string;
}

export interface PublicMeta {
  arenaMaps?: { protocol: 2; defaultChoice: 'random'; enabled: Array<{ id: import('@evolution/shared').ArenaMapId; maxPlayers: number; teamMode: boolean }> };
  roundDurationSeconds?: number;
  brand: {
    name: string;
    shortName: string;
    domain: string;
    tagline: string;
    description: string;
  };
  clientVersion: string;
  configVersion: number;
  arena?: { revision: number; width: number; height: number };
  quickPlaylist?: QuickPlaylistSnapshot;
  maintenance: boolean;
  storage: "memory" | "postgresql" | "durable-local";
  auth: {
    google: {
      enabled: boolean;
      clientId: string | null;
      platforms: readonly ("web" | "android")[];
    };
  };
}

export interface PublicAnnouncement {
  id: string;
  title: string;
  body: string;
  buttonText?: string;
  buttonUrl?: string;
  startAt: string;
  endAt: string;
  priority: number;
}

export interface PublicAnnouncements {
  items: PublicAnnouncement[];
}

export const api = {
  arenaPath: async (): Promise<import("@evolution/shared").ArenaPathStatus> => {
    const { parseArenaPathStatus } = await import("../game/arena-path-status");
    const status = parseArenaPathStatus(await request("/api/profile/arena-path", { cache: "no-store" }));
    if (!status) throw new ApiError(502, "Arena progress is unavailable."); return status;
  },
  selectArenaPath: async (input: { expectedRevision: number; goalId: import("@evolution/shared").ArenaRunGoalId | null; masteryStage: number; cosmeticId: string | null }): Promise<import("@evolution/shared").ArenaPathStatus> => {
    const { parseArenaPathStatus } = await import("../game/arena-path-status");
    const status = parseArenaPathStatus(await request("/api/profile/arena-path", { method: "PUT", body: JSON.stringify(input) }));
    if (!status) throw new ApiError(502, "Arena progress is unavailable."); return status;
  },
  weeklyLeague: async (): Promise<import("@evolution/shared").ArenaWeeklyLeague> => {
    const { parseArenaWeeklyLeague } = await import("../game/arena-path-status");
    const status = parseArenaWeeklyLeague(await request("/api/profile/weekly-league", { cache: "no-store" }));
    if (!status) throw new ApiError(502, "Weekly progress is unavailable."); return status;
  },
  warriorJourney: async (): Promise<import("@evolution/shared").WarriorJourneyStatus> => {
    const status = parseWarriorJourneyStatus(await request("/api/profile/journey", { cache: "no-store" }));
    if (!status) throw new ApiError(502, "Yolculuk bilgisi alınamadı. Tekrar deneyebilirsin.");
    return status;
  },
  rewardConfig: (): Promise<{ config: Omit<RewardHubConfig, "updatedBy"> }> => request("/api/rewards/config"),
  rewards: async (): Promise<RewardHubStatus> => {
    // Reconcile older earned test events without delaying today's ordinary
    // reward screen when the network is slow or an old session has expired.
    if (isNativeAndroidClient()) void reconcileEarnedTestRewards(id => api.rewardAdTestComplete(id));
    return request("/api/rewards");
  },
  rewardDaily: (idempotencyKey: string, dayKey: string): Promise<RewardClaimResult> => request("/api/rewards/daily", { method: "POST", body: JSON.stringify({ idempotencyKey, dayKey }) }),
  rewardSpin: (idempotencyKey: string, dayKey: string, expectedRevision: number): Promise<RewardClaimResult> => request("/api/rewards/wheel", { method: "POST", body: JSON.stringify({ idempotencyKey, dayKey, expectedRevision }) }),
  rewardAdIntent: (action: RewardAdAction, receiptId: string | null): Promise<RewardAdTicket> => request("/api/rewards/ad-intent", { method: "POST", body: JSON.stringify({ action, receiptId }) }),
  rewardAdTestComplete: async (id: string): Promise<{ completed: boolean; profile: PublicProfile }> => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_resolve, reject) => {
      timer = setTimeout(() => { controller.abort(); reject(new DOMException('Test reward verification timed out', 'TimeoutError')); }, 6000);
    });
    try {
      return await Promise.race([
        request<{ completed: boolean; profile: PublicProfile }>(`/api/rewards/ad-intent/${encodeURIComponent(id)}/test-complete`, { method: "POST", body: JSON.stringify({}), signal: controller.signal }),
        timeout,
      ]);
    } finally { clearTimeout(timer); }
  },
  rewardAdCancel: (id: string) => request<{ completed: boolean; cancelled: boolean }>(`/api/rewards/ad-intent/${encodeURIComponent(id)}/cancel`, { method: "POST" }),
  rewardAdStatus: async (id: string): Promise<{ completed: boolean; cancelled: boolean; expired: boolean; xworenBonusTenths?: number; profile: PublicProfile }> => {
    if (isNativeAndroidClient()) await reconcileEarnedTestRewards(intentId => api.rewardAdTestComplete(intentId), id);
    return request("/api/rewards/ad-intent/" + encodeURIComponent(id));
  },
  me: (): Promise<SessionResponse> => request("/api/auth/me"),
  meta: (signal?: AbortSignal): Promise<PublicMeta> => request("/api/meta", { cache: "no-store", ...(signal ? { signal } : {}) }),
  status: (): Promise<PublicArenaStatus> => request("/api/status"),
  guest: (username: string): Promise<SessionResponse> => request("/api/auth/guest", { method: "POST", body: JSON.stringify({ username }) }),
  login: (email: string, password: string, signal?: AbortSignal): Promise<SessionResponse> => request("/api/auth/login", { method: "POST", body: JSON.stringify({ email: normalizeAuthEmail(email), password }), ...(signal ? { signal } : {}) }),
  deletionLogin: (email: string, password: string): Promise<SessionResponse> => request("/api/auth/deletion-login", { method: "POST", body: JSON.stringify({ email, password }) }),
  google: (credential: string, signal?: AbortSignal): Promise<SessionResponse> => request("/api/auth/google", { method: "POST", body: JSON.stringify({ credential }), ...(signal ? { signal } : {}) }),
  googleExisting: (credential: string, signal?: AbortSignal): Promise<SessionResponse> => request("/api/auth/google/existing", { method: "POST", body: JSON.stringify({ credential }), ...(signal ? { signal } : {}) }),
  linkGoogle: (credential: string, currentPassword?: string, signal?: AbortSignal): Promise<SessionResponse> => request("/api/auth/google/link", { method: "POST", body: JSON.stringify({ credential, ...(currentPassword ? { currentPassword } : {}) }), ...(signal ? { signal } : {}) }),
  register: (email: string, username: string, password: string, signal?: AbortSignal): Promise<SessionResponse> => request("/api/auth/register", { method: "POST", body: JSON.stringify({ email: normalizeAuthEmail(email), username: username.trim(), password }), ...(signal ? { signal } : {}) }),
  logout: async (): Promise<void> => {
    const native = isNativeAndroidClient();
    const refreshToken = native ? readNativeSession()?.refreshToken : null;
    await request("/api/auth/logout", {
        method: "POST",
        ...(native && refreshToken ? { body: JSON.stringify({ refreshToken }) } : {}),
    });
    if (native) clearNativeSession();
    await clearGoogleSignInState();
  },
  accountSecurity: async (signal?: AbortSignal): Promise<AccountSecurity> => {
    const result = await request<unknown>("/api/auth/account", signal ? { signal } : {});
    if (!isAccountSecurity(result)) throw new ApiError(502, ACCOUNT_SERVICE_UNAVAILABLE);
    return result;
  },
  profileAvatar: (): Promise<{ profile: PublicProfile; googleAvatarUrl: string | null; uploadAvailable?: boolean }> => request('/api/profile/avatar'),
  updateProfileAvatar: (selection: { source: 'DEFAULT' | 'GOOGLE' } | { source: 'BUILTIN'; avatarId: import('@evolution/shared').ProfileAvatarId }): Promise<{ profile: PublicProfile; googleAvatarUrl: string | null; uploadAvailable?: boolean }> => request('/api/profile/avatar', { method: 'PUT', body: JSON.stringify(selection) }),
  uploadProfileAvatar: (file: Blob, mime: string): Promise<{ profile: PublicProfile; googleAvatarUrl: string | null; uploadAvailable?: boolean }> => request('/api/profile/avatar/upload', { method: 'POST', headers: { 'content-type': mime }, body: file }),
  deleteAccount: async (confirmation: DeleteAccountRequest): Promise<void> => {
    await request<void>("/api/auth/delete-account", { method: "POST", body: JSON.stringify(confirmation) }, 204);
    clearNativeSession();
    try {
      localStorage.removeItem("ea-arena-name");
    } catch { /* Storage may be unavailable. Server-side deletion has completed. */ }
    try {
      sessionStorage.removeItem("varnax.pending-friend-invite.v1");
      sessionStorage.removeItem("varnax.pending-friend-player.v1");
      sessionStorage.removeItem("varnax.pending-party.v1");
    } catch { /* Storage may be unavailable. Server-side deletion has completed. */ }
  },
  friends: (): Promise<{ items: FriendSummary[]; unreadCount?: number }> => request("/api/friends", { cache: "no-store" }),
  socialUpdates: (after?: string, signal?: AbortSignal): Promise<SocialSnapshot> => request(`/api/friends/updates${after ? `?after=${encodeURIComponent(after)}` : ''}`, { cache: 'no-store', ...(signal ? { signal } : {}) }),
  socialPlayer: (publicId: string): Promise<{ player: SocialPlayerProfile }> => request(`/api/players/${encodeURIComponent(publicId)}`, { cache: "no-store" }),
  friendRequests: (): Promise<{ items: SocialFriendRequest[] }> => request("/api/friends/requests", { cache: "no-store" }),
  requestFriend: (publicId: string): Promise<{ player: SocialPlayerProfile }> => request("/api/friends/requests", { method: "POST", body: JSON.stringify({ publicId }) }),
  respondFriendRequest: (requestId: string, action: "accept" | "decline" | "cancel"): Promise<{ ok: true }> => request(`/api/friends/requests/${encodeURIComponent(requestId)}/respond`, { method: "POST", body: JSON.stringify({ action }) }),
  removeFriend: (publicId: string): Promise<{ ok: true }> => request(`/api/friends/players/${encodeURIComponent(publicId)}`, { method: "DELETE" }),
  unblockPlayer: (publicId: string): Promise<{ player: SocialPlayerProfile }> => request(`/api/friends/players/${encodeURIComponent(publicId)}/block`, { method: "DELETE" }),
  blockPlayer: (publicId: string): Promise<{ player: SocialPlayerProfile }> => request(`/api/friends/players/${encodeURIComponent(publicId)}/block`, { method: "PUT", body: JSON.stringify({}) }),
  friendPresence: (online: boolean): Promise<void> => request("/api/friends/presence", { method: "POST", body: JSON.stringify({ online }) }),
  friendConversation: (friendId: string): Promise<FriendConversation> => request(`/api/friends/${encodeURIComponent(friendId)}/messages`),
  markFriendMessagesRead: (friendId: string, throughMessageId: string): Promise<FriendReadReceipt> => request(`/api/friends/${encodeURIComponent(friendId)}/messages/read`, { method: "PUT", body: JSON.stringify({ throughMessageId }) }),
  sendFriendMessage: (friendId: string, text: string, clientId: string): Promise<FriendMessage> => request(`/api/friends/${encodeURIComponent(friendId)}/messages`, { method: "POST", body: JSON.stringify({ text, clientId, rulesAccepted: true }) }),
  sendFriendGameInvite: (friendId: string, input: Omit<import('@evolution/shared').FriendGameInvitation, 'expiresAt' | 'acceptedAt'>, clientId: string): Promise<FriendMessage> => request(`/api/friends/${encodeURIComponent(friendId)}/game-invites`, { method: 'POST', body: JSON.stringify({ ...input, clientId, rulesAccepted: true }) }),
  acceptFriendGameInvite: (friendId: string, messageId: string): Promise<FriendMessage> => request(`/api/friends/${encodeURIComponent(friendId)}/game-invites/${encodeURIComponent(messageId)}/accept`, { method: 'POST', body: JSON.stringify({}) }),
  blockFriend: (friendId: string, blocked: boolean): Promise<FriendConversation> => request(`/api/friends/${encodeURIComponent(friendId)}/block`, { method: "PUT", body: JSON.stringify({ blocked }) }),
  reportFriendMessage: (friendId: string, messageId: string): Promise<{ reported: true }> => request(`/api/friends/${encodeURIComponent(friendId)}/messages/report`, { method: "POST", body: JSON.stringify({ messageId }) }),
  currentPrivateLobby: (): Promise<{ lobby: PrivateLobbySnapshot | null }> => request("/api/private-lobbies/current", { cache: "no-store" }),
  createPrivateLobby: (settings: PrivateLobbySettings): Promise<{ lobby: PrivateLobbySnapshot }> => request("/api/private-lobbies", { method: "POST", body: JSON.stringify(settings) }),
  updatePrivateLobbySettings: (settings: PrivateLobbySettings, expectedRevision: number): Promise<{ lobby: PrivateLobbySnapshot }> => request("/api/private-lobbies/settings", { method: "PUT", body: JSON.stringify({ ...settings, expectedRevision }) }),
  joinPrivateLobby: (code: string): Promise<{ lobby: PrivateLobbySnapshot }> => request("/api/private-lobbies/join", { method: "POST", body: JSON.stringify({ code }) }),
  setPrivateLobbyTeam: (team: PrivateLobbyTeam): Promise<{ lobby: PrivateLobbySnapshot }> => request("/api/private-lobbies/team", { method: "PUT", body: JSON.stringify({ team }) }),
  setPrivateLobbyReady: (ready: boolean): Promise<{ lobby: PrivateLobbySnapshot }> => request("/api/private-lobbies/ready", { method: "PUT", body: JSON.stringify({ ready }) }),
  startPrivateLobby: (): Promise<{ lobby: PrivateLobbySnapshot }> => request("/api/private-lobbies/start", { method: "POST", body: "{}" }),
  kickPrivateLobbyMember: (userId: string): Promise<{ lobby: PrivateLobbySnapshot }> => request("/api/private-lobbies/kick", { method: "POST", body: JSON.stringify({ userId }) }),
  leavePrivateLobby: (): Promise<{ lobby: PrivateLobbySnapshot | null }> => request("/api/private-lobbies/leave", { method: "POST", body: "{}" }),
  createFriendInvite: (): Promise<FriendInviteCreated> => request("/api/friends/invites", { method: "POST", body: "{}" }),
  acceptFriendInvite: (inviteCode: string): Promise<FriendInviteAccepted> => request("/api/friends/invites/accept", { method: "POST", body: JSON.stringify({ inviteCode }) }),
  ticket: async (mode: GameMode, signal?: AbortSignal, partyCode?: string, matchNickname?: string, quickPlaylistId?: string, mapChoice?: import('@evolution/shared').ArenaMapChoice): Promise<MatchTicket> => {
    // Read at admission time, including re-entry after a deployment/rollback.
    // No cached profile or installed version can authorize a server feature.
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) abort();
    signal?.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(abort, 8000);
    let capabilities: ReturnType<typeof selectArenaEntryCapabilities>;
    try {
      if (controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');
      const meta = await request<PublicMeta>('/api/meta', { cache: 'no-store', signal: controller.signal });
      if (controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');
      capabilities = selectArenaEntryCapabilities(meta, mode, mapChoice);
    } finally { clearTimeout(timeout); signal?.removeEventListener('abort', abort); }
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    return request('/api/match/ticket', { method: 'POST', body: JSON.stringify({ mode, arenaRevision: 3, reserveSeat: true, ...capabilities, ...(partyCode ? { partyCode } : {}), ...(matchNickname ? { matchNickname } : {}), ...(quickPlaylistId ? { quickPlaylistId } : {}) }), ...(signal ? { signal } : {}) });
  },
  flags: (): Promise<FeatureFlags> => request("/api/feature-flags"),
  content: <T>(): Promise<T> => request("/api/content"),
  announcements: (): Promise<PublicAnnouncements> => request("/api/announcements"),
  androidUpdate: (): Promise<{ notice: AndroidUpdateNotice | null }> => request("/api/android-update", { cache: "no-store" }),
  profile: (): Promise<{ profile: PublicProfile }> => request("/api/profile"),
  economy: (): Promise<{ config: PublicEconomyConfig }> => request("/api/economy", { cache: "no-store" }),
  renameProfile: (displayName: string, expectedRenameCount: number, expectedPriceDiamonds: number, idempotencyKey: string): Promise<{ profile: PublicProfile; changed: boolean; chargedDiamonds: number; freeRenamesRemaining: number }> => request("/api/profile/rename", { method: "POST", body: JSON.stringify({ displayName, expectedRenameCount, expectedPriceDiamonds, idempotencyKey }) }),
  purchaseDiamond: (offerId: string, expectedPriceDiamonds: number, idempotencyKey: string): Promise<{ owned: boolean; diamonds: number; grantedCosmeticIds: string[] }> => request("/api/shop/diamond-purchase", { method: "POST", body: JSON.stringify({ offerId, expectedPriceDiamonds, idempotencyKey }) }),
  claimQuest: async (questId: string, periodKey: string, idempotencyKey: string): Promise<{ profile: PublicProfile; diamonds: number; gold: number; claimed: boolean }> => {
    const result = await request<{ profile: PublicProfile; diamonds: number; gold: number; claimed: boolean }>(`/api/quests/${encodeURIComponent(questId)}/claim`, { method: "POST", body: JSON.stringify({ periodKey, idempotencyKey }) });
    notifyQuestRewardsChanged(result.profile.id);
    return result;
  },
  inventory: (): Promise<{ owned: string[]; loadout: Record<string, string> }> => request("/api/inventory"),
  updateLoadout: (loadout: Record<string, string>): Promise<{ loadout: Record<string, string> }> => request("/api/loadout", { method: "PUT", body: JSON.stringify(loadout) }),
  purchase: (cosmeticId: string, expectedPriceGold?: number): Promise<{ owned: boolean; gold: number }> => request("/api/shop/purchase", { method: "POST", body: JSON.stringify({ cosmeticId, ...(expectedPriceGold !== undefined ? { expectedPriceGold } : {}), idempotencyKey: createSecureUuid() }) }),
  claimDaily: (): Promise<{ day: number; gold: number; claimedAt: string }> => request("/api/daily-reward/claim", { method: "POST", body: JSON.stringify({ idempotencyKey: createSecureUuid() }) }),
  quests: <T>(): Promise<T> => request("/api/quests"),
  leaderboard: <T>(category = "score", page = 1): Promise<T> => request(`/api/leaderboards?category=${encodeURIComponent(category)}&page=${page}`),
  report: (targetUserId: string, reason: string, details = ""): Promise<void> => request("/api/reports", { method: "POST", body: JSON.stringify({ targetUserId, reason, details }) }),
};
